import { Controller } from "@hotwired/stimulus"
import { segmentsFor } from "../../../utils/sms_segments"

// Owns the message-composition UI: preset/custom tab switching and the live
// segment counter. Emits a `changed` event whenever the mode or message body
// changes so the surrounding broadcast controller can refresh its cost estimate.
export default class extends Controller {
  static targets = [
    "customBody",
    "customEditor",
    "customPanel",
    "customTab",
    "kind",
    "presetPanel",
    "presetTab",
    "segmentCount",
    "smartFields",
    "templatePreview",
    "templateSelect",
  ]

  connect() {
    this.updateSegmentCount()
    this.syncTemplate()
  }

  // Templates need per-lease delivery, so switching to per customer drops the
  // chosen template and brings back the typed message.
  selectDeliveryBasis(event) {
    if (event.target.value !== "lease" && this.hasTemplateSelectTarget) {
      this.templateSelectTarget.value = ""
    }

    this.syncTemplate()
  }

  selectMode(event) {
    const kind = event.params.kind
    if (this.hasKindTarget) this.kindTarget.value = kind

    const custom = kind === "custom"
    if (this.hasPresetPanelTarget) this.presetPanelTarget.classList.toggle("hidden", custom)
    if (this.hasCustomPanelTarget) this.customPanelTarget.classList.toggle("hidden", !custom)
    if (this.hasPresetTabTarget) this.presetTabTarget.toggleAttribute("data-active", !custom)
    if (this.hasCustomTabTarget) this.customTabTarget.toggleAttribute("data-active", custom)

    this.notifyChanged()
  }

  insertSmartField(event) {
    const dropdown = event.target.closest("[data-controller='dropdown']")
    dropdown.dataset.dropdownOpenValue = false

    const textarea = this.customBodyTarget
    textarea.setRangeText(
      event.target.dataset.value.trim(),
      textarea.selectionStart,
      textarea.selectionEnd,
      "end"
    )
    textarea.focus()

    this.updateComposeState()
  }

  // Fires on textarea input: update the counter immediately and debounce the
  // authoritative server-side cost refresh.
  updateComposeState() {
    this.updateSegmentCount()

    clearTimeout(this.composeDebounce)
    this.composeDebounce = setTimeout(() => this.notifyChanged(), 400)
  }

  updateSegmentCount() {
    if (!this.hasSegmentCountTarget) return

    const body = this.hasCustomBodyTarget ? this.customBodyTarget.value : ""
    this.segmentCountTarget.textContent = segmentsFor(body)
  }

  notifyChanged() {
    this.dispatch("changed")
  }

  selectTemplate() {
    this.syncTemplate()
    this.notifyChanged()
  }

  // A chosen template replaces the editor with its read-only text. The typed
  // message is only hidden, so choosing None brings it back unchanged.
  syncTemplate() {
    const templateId = this.hasTemplateSelectTarget ? this.templateSelectTarget.value : ""
    const templateChosen = templateId !== ""

    this.customEditorTarget.classList.toggle("hidden", templateChosen)
    this.smartFieldsTarget.classList.toggle("hidden", templateChosen)
    this.templatePreviewTargets.forEach((preview) => {
      preview.classList.toggle("hidden", preview.dataset.templateId !== templateId)
    })
  }
}
